import Configuration from '@/agilis/util/Configuration'
import jsonLogic from '@/plugins/jsonLogic'
import L from 'lodash'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'TrackerActionsMixin',

  computed: {
    ...mapGetters({
      selectedContext: 'contextStore/selectedContext',
      trackerStateMachine: 'contextStore/trackerStateMachine',
      trackerType: 'contextStore/trackerType',
      trackerDataTable: 'contextStore/trackerDataTable',
      userId: 'userDataStore/userId'
    }),

    jlContext () {
      return Object.assign(
        {},
        this.trackerDTO,
        this.jlAssertionContext
      )
    },

    actionArray () {
      return Configuration.auth.getEventsFromState(this.trackerDTO).map(a => {
        const validationLogic = a.validationLogic && a.validationLogic.jsonLogic

        // see https://jsonlogic.com
        let validationOutput
        try {
          validationOutput = validationLogic ? jsonLogic.apply(validationLogic, this.jlContext) : true
        } catch (e) { // catch unsupportd logics
          console.log('Unsupported json-logic', e)
          validationOutput = false
        }
        const invalid = validationOutput !== true

        return Object.assign({}, a, {
          invalid,
          errorMessage: invalid ? (
            typeof validationOutput === 'string' ? this.$t(validationOutput) : this.$t('actionValidationKo')
          ) : null
        })
      })
    }
  },

  methods: {
    ...mapActions({
      'notifyError': 'notificationStore/notifyError',
      'notifyMessage': 'notificationStore/notifyMessage',
      'notifyUserConfirm': 'notificationStore/notifyUserConfirm',
      'startLoader': 'loaderStore/startLoader',
      'stopLoader': 'loaderStore/stopLoader'
    }),

    actionPopupData (formValues = {}) {
      const trackerTypeName = this.$t(this.trackerType.name)
      return {
        trackerTypeName,
        ...Object.assign({}, this.trackerDTO, formValues)
      }
    },

    formFields (actionColumns = {}) {
      // Datatable columns
      const datatableColumns = actionColumns ? L.merge(L.cloneDeep(this.trackerDataTable.columns), actionColumns) : L.cloneDeep(this.trackerDataTable.columns)
      for (const colConfig of Object.values(datatableColumns)) {
        if (colConfig.url) { // insert tracker context in api paths where necessary
          colConfig.url = this.$axios.agilisUrlAddContext(colConfig.url, this.selectedContext)
        }
        if (colConfig.api) {
          colConfig.api.url = this.$axios.agilisUrlAddContext(colConfig.api.url, this.selectedContext)
        }
      }

      // Form graphical elements
      const formElements = this.trackerDataTable.formElements
      return {
        ...datatableColumns,
        ...formElements
      }
    },

    /**
    * Gets user confirmation.
    *
    * @param {object} eventConfig - The event configuration.
    * @returns {Promise} A promise that resolves with the user's response.
    */
    getUserConfirmation (eventConfig) {
      if (eventConfig?.userPopup && (eventConfig?.userPopup?.messages?.length > 0 || eventConfig?.userPopup?.dataTable)) {
        return this.notifyUserConfirm({
          title: eventConfig?.label,
          ...eventConfig.userPopup,
          form: eventConfig.userPopup.dataTable,
          data: this.actionPopupData(eventConfig.userPopup.dataTable?.formValues)
        })
      }

      return Promise.resolve(true)
    },

    /**
     * Processes the userPopup messages which are jsonLogic expressions.
    * userPopup message could either be a string or a jsonLogic expression defined in configuration.
    * If any message is not a string but a jsonLogic expression, it will be evaluated to get a plain string.
    *
    * @param {object} eventConfig - The event configuration.
    */
    processJsonLogicMessages (eventConfig) {
      if (eventConfig?.userPopup?.messages) {
        let messages = eventConfig?.userPopup?.messages
        messages.forEach((msg, i) => {
          if (typeof msg !== 'string' && msg.hasOwnProperty('jsonLogic')) {
            let tmpMsg = this.$t(jsonLogic.apply(msg.jsonLogic, this.jlContext))
            tmpMsg ? eventConfig.userPopup.messages[i] = tmpMsg : eventConfig.userPopup.messages.splice(i, 1)
          }
        })
      }
      return eventConfig
    },

    async executeAction (eventConfig, userConfirmResponse, actionId) {
      this.startLoader({ id: 'change-tracker-state' })
      // the user confirmed the action
      const eventPayload = userConfirmResponse !== true ? userConfirmResponse : {}
      const url = this.$axios.agilisUrlTrackerAction(
        this.trackerDTO.applicationId, this.trackerDTO.trackerTypeId, this.trackerDTO.id, actionId)
      try {
        const response = await this.$axios.post(url, eventPayload, this.$axios.agilisRequestConfig(url))
        const eventName = this.$t(Configuration.state.getTrackerEventsMap(this.trackerDTO)[actionId].label)
        const stateName = this.$t(Configuration.state.getTrackerStatesMap(this.trackerDTO)[response.data.state].label)
        const message = response.data.transitionMessage
          ? this.$t('message.actionSuccess', { action: eventName, state: stateName }).concat('\n\n' + response.data.transitionMessage)
          : this.$t('message.actionSuccess', { action: eventName, state: stateName })
        this.notifyMessage(message)
        if (eventConfig?.redirect?.routeName) {
          // redirect to the route specified in the event configuration
          this.$router.push({ name: eventConfig.redirect.routeName })
        } else if (eventConfig?.redirect?.method?.name) {
          // call the router method specified in the event configuration
          const args = eventConfig.redirect.method.args || []
          this.$router.agilis[eventConfig.redirect.method.name](...args)
        } else {
          // emit the change-tracker-state event to the parent component
          this.$emit('change-tracker-state', response.data.state)
        }
      } catch (error) {
        // error occurred, stop the loader and notify the error
        this.notifyError(error)
        this.$emit('change-tracker-state', null)
      }
    },

    /**
    * Handles the click event on an action in a workflow.
    * Given the action clicked the action ID is used to retrieve the actions's event configurations,
    * displaying a user popup if necessary based on the event configuration, and executing the action.
    * The actions and event behaviors are defined in the tracker's state machine configuration.
    *
    * @param {String} actionId - the id of the action clicked
    */
    async handleActionClick (actionId) {
      const eventConfig = Configuration.state.getTrackerEventsMap(this.trackerDTO)[actionId]

      const eventConfigWithProcessMessages = this.processJsonLogicMessages(L.cloneDeep(eventConfig)) // send a copy of evenConfig so it gets modified safely

      const dataTable = eventConfigWithProcessMessages?.userPopup?.dataTable
      if (dataTable) {
        // retrieve the form fields for display in userPopup
        dataTable.fields = this.formFields(dataTable.columns)
      }

      const userResponse = await this.getUserConfirmation(eventConfigWithProcessMessages)

      if (userResponse) {
        await this.executeAction(eventConfigWithProcessMessages, userResponse, actionId)
      }
    },

    userCanAccessAction (actionId) {
      return Configuration.auth.userCanAccessAction(this.selectedContext, actionId)
    }
  }
}
